/**
 * Google Fonts configuration and utilities
 * Used for dynamic font loading in templates
 */

// Font category for grouping in selector
export type FontCategory = 'serif' | 'sans-serif' | 'display' | 'cursive'

export interface GoogleFont {
  id: string
  name: string
  heading: string
  body: string
  googleFont: string
  category?: FontCategory
  suitedFor?: string[]
}

// Google Fonts to be available
export const AVAILABLE_GOOGLE_FONTS: GoogleFont[] = [
  {
    id: 'playfair-dm-sans',
    name: 'Playfair + DM Sans',
    heading: "'Playfair Display', serif",
    body: "'DM Sans', sans-serif",
    googleFont: "Playfair+Display:wght@400;500;600;700&family=DM+Sans:wght@400;500;700",
    category: 'serif',
    suitedFor: ['beauty', 'creative', 'photographer', 'artist-painter', 'makeup-artist', 'esthetician', 'florist'],
  },
  {
    id: 'cormorant-nunito',
    name: 'Cormorant + Nunito',
    heading: "'Cormorant', serif",
    body: "'Nunito', sans-serif",
    googleFont: "Cormorant:wght@400;500;600;700&family=Nunito:wght@400;500;700",
    category: 'serif',
    suitedFor: ['beauty', 'creative', 'florist', 'event-planner'],
  },
  {
    id: 'montserrat-open-sans',
    name: 'Montserrat + Open Sans',
    heading: "'Montserrat', sans-serif",
    body: "'Open Sans', sans-serif",
    googleFont: "Montserrat:wght@400;500;600;700&family=Open+Sans:wght@400;500;700",
    category: 'sans-serif',
    suitedFor: ['trade', 'tutor-teacher', 'personal-trainer', 'electrician', 'plumber', 'hvac-technician', 'carpenter'],
  },
  {
    id: 'fraunces-work-sans',
    name: 'Fraunces + Work Sans',
    heading: "'Fraunces', serif",
    body: "'Work Sans', sans-serif",
    googleFont: "Fraunces:wght@400;500;600;700&family=Work+Sans:wght@400;500;700",
    category: 'serif',
    suitedFor: ['creative', 'photographer', 'artist-painter'],
  },
  // Additional fonts for social templates
  {
    id: 'bebas-nunito',
    name: 'Bebas Neue + Nunito',
    heading: "'Bebas Neue', cursive",
    body: "'Nunito', sans-serif",
    googleFont: "Bebas+Neue&family=Nunito:wght@400;500;700",
    category: 'display',
    suitedFor: ['social', 'bold', 'attention-grabbing'],
  },
  {
    id: 'oswald-lato',
    name: 'Oswald + Lato',
    heading: "'Oswald', sans-serif",
    body: "'Lato', sans-serif",
    googleFont: "Oswald:wght@400;500;600;700&family=Lato:wght@400;500;700",
    category: 'sans-serif',
    suitedFor: ['trade', 'personal-trainer', 'construction'],
  },
  {
    id: 'poppins-lato',
    name: 'Poppins + Lato',
    heading: "'Poppins', sans-serif",
    body: "'Lato', sans-serif",
    googleFont: "Poppins:wght@400;500;600;700&family=Lato:wght@400;500;700",
    category: 'sans-serif',
    suitedFor: ['beauty', 'wellness', 'modern'],
  },
  {
    id: 'raleway-montserrat',
    name: 'Raleway + Montserrat',
    heading: "'Raleway', sans-serif",
    body: "'Montserrat', sans-serif",
    googleFont: "Raleway:wght@400;500;600;700&family=Montserrat:wght@400;500;700",
    category: 'sans-serif',
    suitedFor: ['creative', 'professional', 'elegant'],
  },
]

/**
 * Get font by ID
 */
export function getFontById(fontId: string): GoogleFont | undefined {
  return AVAILABLE_GOOGLE_FONTS.find(font => font.id === fontId)
}

/**
 * Get fonts by category
 */
export function getFontsByCategory(category: FontCategory): GoogleFont[] {
  return AVAILABLE_GOOGLE_FONTS.filter(font => font.category === category)
}

/**
 * Get all font IDs
 */
export function getAllFontIds(): string[] {
  return AVAILABLE_GOOGLE_FONTS.map(font => font.id)
}

/**
 * Build Google Fonts URL for loading
 */
export function buildGoogleFontsUrl(fontIds: string[]): string {
  const fonts = fontIds
    .map(id => getFontById(id))
    .filter((font): font is GoogleFont => font !== undefined)

  if (fonts.length === 0) {
    return ''
  }

  const googleFontParams = fonts.map(font => font.googleFont).join('&')
  return `https://fonts.googleapis.com/css2?${googleFontParams}&display=swap`
}

/**
 * Build Google Fonts URL for a single font
 */
export function buildSingleFontUrl(fontId: string): string {
  return buildGoogleFontsUrl([fontId])
}
